import React from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  as?: 'button' | 'a';
  href?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:   'bg-v2-green-500 text-white hover:bg-v2-green-600 border-transparent',
  secondary: 'bg-transparent text-v2-green-500 border-v2-green-400 hover:bg-v2-green-50',
  ghost:     'bg-v2-neutral-100 text-v2-neutral-700 border-transparent hover:bg-v2-neutral-200',
  danger:    'bg-v2-red-400 text-white border-transparent hover:bg-v2-red-500',
  outline:   'bg-transparent text-v2-neutral-700 border-v2-neutral-300 hover:bg-v2-neutral-50',
};

// Per design system spec:
// sm: 9px/18px @ 13px font, md: 14px/28px @ 15px, lg: 18px/36px @ 16px
const sizeClasses: Record<Size, string> = {
  sm: 'px-[18px] py-[9px] text-[13px]',
  md: 'px-[28px] py-[14px] text-[15px]',
  lg: 'px-[36px] py-[18px] text-[16px]',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  loading,
  children,
  className = '',
  disabled,
  as: Tag = 'button',
  href,
  ...rest
}) => {
  const base =
    'inline-flex items-center gap-2 font-semibold rounded-v2-full border-[1.5px] cursor-pointer transition-all duration-150 font-sans select-none disabled:opacity-50 disabled:cursor-not-allowed';

  const classes = `${base} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  if (Tag === 'a') {
    return (
      <a href={href} className={classes} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {loading ? <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" /> : leftIcon}
        {children}
        {rightIcon}
      </a>
    );
  }

  return (
    <button className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" /> : leftIcon}
      {children}
      {rightIcon}
    </button>
  );
};
